import { Application_Status_Enum, WebappApplicationFragment } from '@gql'
import { GroupingState } from '@tanstack/react-table'
import React from 'react'
import { Table } from '@tanstack/react-table'
import { Input } from '@/components/ui/input'
import { useTranslation } from 'react-i18next'
import { TableGroupBySelect } from '@/common/molecules/TableGroupBySelect'
import {
  TableFacetedFilter,
  TableFilter,
} from '@/common/molecules/TableFacetedFilters'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

type ApplicationsTableToolbarProps = {
  table: Table<WebappApplicationFragment>
  grouping: GroupingState
  setGrouping: (value: GroupingState) => void
  setShowNonEditedApplications: (value: boolean) => void
  showNonEditedApplications: boolean
  estateRoundNumbers: number[]
}

export const ApplicationsTableToolbar = ({
  grouping,
  setGrouping,
  table,
  setShowNonEditedApplications,
  showNonEditedApplications,
  estateRoundNumbers,
}: ApplicationsTableToolbarProps) => {
  const { t } = useTranslation()

  const statusOptions: TableFilter[] = Object.values(
    Application_Status_Enum
  ).map((status) => ({
    value: status,
    label: t(`common.applicationStatus.${status}`),
  }))

  const roundOptions: TableFilter[] = estateRoundNumbers.map((round) => ({
    value: round.toString(),
    label: round.toString(),
  }))

  const groupByOptions: TableFilter[] = [
    {
      value: 'status',
      label: t('ApplicationsTableToolbar.facetedFilter.status'),
    },
    {
      value: 'createdAt',
      label: t('ApplicationsTableToolbar.facetedFilter.date'),
    },
    {
      value: 'offerAmount',
      label: t('ApplicationsTableToolbar.facetedFilter.offerAmount'),
    },
    {
      value: 'score',
      label: t('ApplicationsTableToolbar.facetedFilter.score'),
    },
  ]

  const setGroupByValue = (value: string) => {
    setGrouping([value])
  }

  return (
    <div className="tw-flex tw-justify-between tw-items-center tw-py-4 tw-gap-2">
      <div className="tw-flex tw-items-center tw-space-x-2 tw-w-full">
        <Input
          placeholder={t('ApplicationsTableToolbar.searchPlaceholder')}
          value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
          onChange={(event) =>
            table.getColumn('name')?.setFilterValue(event.target.value)
          }
          className="tw-max-w-sm"
        />
        {table.getColumn('status') && (
          <TableFacetedFilter
            column={table.getColumn('status')}
            title="Status"
            options={statusOptions}
          />
        )}
        {table.getColumn('round') && (
          <TableFacetedFilter
            column={table.getColumn('round')}
            title="Tour"
            options={roundOptions}
          />
        )}
        <div className="tw-flex tw-items-center tw-space-x-2">
          <Checkbox
            checked={showNonEditedApplications}
            onCheckedChange={setShowNonEditedApplications}
          />
          <Label>
            {t('ApplicationsTableToolbar.showNonEditedApplications')}
          </Label>
        </div>
      </div>
      <TableGroupBySelect
        groupingValue={grouping?.[0]}
        setGroupingValue={setGroupByValue}
        options={groupByOptions}
      />
    </div>
  )
}
